import { useEffect, useState } from 'react';
import axios from 'axios';
import { ServerDownPage } from './ServerDownPage';

interface ServerHealthCheckProps {
  children: React.ReactNode;
}

export const ServerHealthCheck: React.FC<ServerHealthCheckProps> = ({ children }) => {
  const [isServerUp, setIsServerUp] = useState<boolean | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  const checkServerHealth = async () => {
    try {
      const baseURL = import.meta.env.VITE_API_URL || 'https://go.sentra.be';
      // Create a standalone axios instance to avoid interceptors
      const response = await axios.get(`${baseURL}/ping`, {
        timeout: 5000, // 5 second timeout
        validateStatus: (status) => status < 500, // Don't throw on 4xx errors
      });
      
      // Check if we got "pong" back
      if (response.data === 'pong' || response.data?.message === 'pong') {
        setIsServerUp(true);
        setRetryCount(0);
      } else {
        setIsServerUp(false);
      }
    } catch (error) {
      console.error('Server health check failed:', error);
      setIsServerUp(false);
    }
  };

  useEffect(() => {
    // Initial check
    checkServerHealth();

    // Set up periodic checks when server is down
    if (isServerUp === false) {
      const interval = setInterval(() => {
        checkServerHealth();
        setRetryCount((prev) => prev + 1);
      }, 10000); // Check every 10 seconds

      return () => clearInterval(interval);
    }
  }, [isServerUp]);

  // Show loading state initially
  if (isServerUp === null) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center">
          <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
          <p className="mt-4 text-lg text-muted-foreground">Checking server status...</p>
        </div>
      </div>
    );
  }

  // Show server down page if server is not responding
  if (!isServerUp) {
    return <ServerDownPage onRetry={checkServerHealth} retryCount={retryCount} />;
  }

  // Server is up, render the app
  return <>{children}</>;
};
